// Package imports:
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
// Component imports:
import Button from '../../ui-elements/Button/Button';
import Input from '../../ui-elements/Input/Formik/Input';
import Loading from '../../ui-elements/Loading/Loading';
import Link from '../../ui-elements/Link/Link';
import Alert from '../../ui-elements/Alert/Alert';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import DisplayBox from '../DisplayBox/DisplayBox';
import Table from '../../ui-elements/Table/Table';
import HistoryButtonDropDown from '../HistoryButtonDropDown/HistoryButtonDropDown';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
import {DatePickerField} from '../DatePicker/Formik/DatePicker'; 
import FilterItem from '../../ui-elements/Filter/FilterItem/FilterItem';
// Service imports:
import { GET_KELDAN_API_URL } from '../../services/config';
import { getTableDate } from '../../services/utils';
import { useBuyingProcessUrl } from '../../services/buyhook';
import { ErrorMessages } from '../../services/errorMessages';
// Type imports:
import { IKeldanApiResponse } from '../../types/KeldanTypes';
import { LegalAdvert, LegalSearchViewModel } from '../../types/LegalTypes';

interface IAdvertTypesFilter {
    type: string,
    isOn: boolean
}

type DataStatus = 'no filter selected' | 'complete' | 'no data' ;

interface IDataMemo {
    dataToDisplay: LegalAdvert[] | null,
    dataStatus: DataStatus
};
interface IProps {
    isAuthenticated: boolean
}

const LegalSearch: React.FC<IProps> = ({ isAuthenticated }) => {
    const [ data, setData] = useState<LegalAdvert[]>();
    const [ types, setTypes] = useState<IAdvertTypesFilter[]>();
    const [ searchTerm, setSearchTerm] = useState('');

    const usingSourceFilter = useMemo(() => {
        if (types === null || types === undefined || types instanceof Error) return 'all';
        if (types.every(types => !types.isOn)) return 'none';
        if (types.every(types => types.isOn)) return 'all';
        return 'some';
    }, [ types ]);

    const isSearchLoading = useMemo(() => {
        return (data === undefined && searchTerm.length > 0)
    }, [ data, searchTerm ])

    const { tryPurchaseItem, isItemLoading, setToastError, ModalsAndToasts } = useBuyingProcessUrl((legalAdvert: LegalAdvert, reference: string) => {
        const { advertNumber } = legalAdvert;
        const url = (reference === '') 
            ? `${GET_KELDAN_API_URL()}/Legal/BuyReport/?adNumber=${advertNumber}`
            :`${GET_KELDAN_API_URL()}/Legal/BuyReport/?adNumber=${advertNumber}&reference=${reference}`
        return url;
        },
        () => {},
        'Lögbirtingablaðinu'
    );

    const handleSearch = async (values: IFormValues) => {
        setData(undefined);
        setSearchTerm(values.term)
        try {
            const requestQuery = new URLSearchParams({
                term: values.term,
            })
            if(values.dateFrom) requestQuery.append('FromDateString', getTableDate(values.dateFrom,"DD/MM/YYYY", "."))
            if(values.dateTo) requestQuery.append('ToDateString', getTableDate(values.dateTo,"DD/MM/YYYY", "."))
            const url = `${GET_KELDAN_API_URL()}/Legal/Search`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if(response.ok){
                //user is not logged in -> redirect to login page
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse<LegalSearchViewModel> = await response.json();
                
                if (responseBody.success) {
                    if(responseBody.result) {
                        setData(responseBody.result.result);
                        const newTypeList: string[] = [];
                        if(responseBody.result.typeList.advertTypes !== undefined && responseBody.result.result.length > 0){
                            for(let x of responseBody.result.typeList.advertTypes){
                                if(responseBody.result.result.find(y => y.type === x)){
                                    newTypeList.push(x)
                                }
                            }
                        }
                        const filteredResultsFilters: IAdvertTypesFilter[] = newTypeList.map(type => ({
                            type,
                            isOn: true
                        }));
                        setTypes(filteredResultsFilters);
                    }
                } else {
                    const { message } = responseBody;
                    if (message === ErrorMessages.NoCard) {
                        setToastError('no card');
                    }
                    if (message) {
                        setToastError({
                            type: 'alert',
                            headText: message
                        })
                    } else {
                        setToastError('bad request')
                    }
                }
            } else {
                setToastError('response not ok')
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            setToastError('network error')
        }
    }

    const subFormComponent = useMemo(() => {
        return ((props: { values: IFormValues }) => (
            <SubFormComponent
                values={props.values}
                loading={isSearchLoading}
                isAuthenticated={isAuthenticated}
            />
        ))
    }, [isSearchLoading, isAuthenticated]);

    const dataToDisplay = useMemo(() => {
        //initialize obj
        const dataToReturn: IDataMemo = {
            dataToDisplay: [],
            dataStatus: 'no data'
        }
        if(data?.length === 0) return dataToReturn;

        //changing status. Since data exists. 
        dataToReturn.dataStatus = 'no filter selected'
        // if no source filter selected -> then there is nothing that 'isOn' so no data is returned
        //'no filter selected' to show that there is data but none can be shown
        if (usingSourceFilter === 'none') return dataToReturn;

        if(data !== undefined && types !== undefined){
            for(let item of data){
                //loop through every instance of data and check if for a specific type if it's marked as on
                //if it is marked. Then push to array.
                if(types.find(x => x.isOn && x.type === item.type)){
                    dataToReturn.dataToDisplay?.push(item)
                }
            }
            //completed looping through
            dataToReturn.dataStatus = "complete";
        }
        return dataToReturn;
    }, [types])

    return (
        <div className='main KCL_legal-search'>
            <div className='main__inner-fluid'>
                <div className="shell">
                    <HeadSection
                        title={
                            <>
                                <h1>Lögbirtingablaðið</h1>
                                {(isAuthenticated)
                                    ? <HistoryButtonDropDown />
                                    : null
                                }
                            </>
                        }
                        description={
                            <>
                                <p className='summary-paragraph'>Hér getur þú flett upp auglýsingum úr Lögbirtingablaðinu. </p>
                                <p className='paragraph'>
                                    Þú getur leitað eftir einu eða fleiri leitarorðum og aðgreint með kommu á milli, 
                                    til dæmis “010101-1239, nafn fyrirtækis” og einnig valið tímabil. 
                                    Hægt er svo að sía niðurstöður eftir flokkum.
                                    <Link linkSize='18'  url='/Verdskra#verdskra'>Sjá verðskrá.</Link>
                                </p>
                            </>
                        }
                    />
                    <div className='formikWrapper'>
                        <Formik
                            initialValues={{
                                term: '',
                                dateFrom: '',
                                dateTo: '',
                            }}
                            validationSchema={Yup.object({
                                term: Yup.string().required('Vinsamlegast sláið inn nafn eða kennitölu'),
                                dateFrom: Yup.date().nullable().when('dateTo', {
                                    is: (v: any) => v != null,
                                    then: s => s.max(Yup.ref('dateTo'), `Dagsetning má ekki vera á eftir til dagsetningu`),
                                }),
                            })}
                            onSubmit={ (e) => handleSearch(e)}
                            component={subFormComponent}
                        />
                    </div>
                </div>
                {(searchTerm) && (
                    <div className="section--gray">
                        <div className="shell">
                            <div className='title-wrapper'>
                                <h2>Leitarniðurstöður fyrir {searchTerm}</h2>
                                <FontAwesomeIcon onClick={() => setSearchTerm('')} icon={faXmark} className="icon-fa"/>
                            </div>
                            <DisplayBox>
                                {(types === undefined)
                                    ? null
                                    : <div className='source-filters'>
                                        <strong>Sía eftir tegund auglýsingar:</strong>
                                        <div className='filterWrapper'>
                                            <FilterItem
                                                showCheck
                                                size='lg'
                                                text="Velja allar"
                                                selected={usingSourceFilter === 'all'}
                                                toggleSelected={() => {
                                                    const sourcesCopy: IAdvertTypesFilter[] = [];
                                                    types.forEach((s) => {
                                                        sourcesCopy.push({
                                                            type: s.type,
                                                            isOn: usingSourceFilter !== 'all'
                                                        });
                                                    });
                                                    setTypes(sourcesCopy);
                                                }}
                                            />
                                            {types.map(({type, isOn }, i) =>
                                                <FilterItem
                                                    showCheck
                                                    size='lg'
                                                    selected={isOn}
                                                    text={type}
                                                    toggleSelected={() => {
                                                        // Copy to prevent corruption of state.
                                                        const sourcesCopy = [...types];
                                                        sourcesCopy.splice(i, 1, {
                                                            type,
                                                            isOn: !isOn
                                                        });
                                                        setTypes(sourcesCopy);
                                                    }}
                                                    key={i}
                                                />
                                            )}
                                        </div>
                                    </div>
                                }
                                {(dataToDisplay.dataToDisplay !== null && dataToDisplay.dataStatus === 'complete')
                                    ? <Table
                                        tableSize='lg'
                                        data={dataToDisplay.dataToDisplay}
                                        columns={[
                                            {
                                                title: 'dagsetning',
                                                dataAccessor: ({ published }) => getTableDate(published, "DD/MM/YYYY", "."),
                                                textAlign: 'left',
                                                simpleSortable: ({ published }) => published,
                                            }, {
                                                title: 'tegund',
                                                dataAccessor: ({ type }) => type,
                                                textAlign: 'left',
                                                simpleSortable: ({ type }) => type
                                            }, {
                                                title: 'efni',
                                                dataAccessor: ({ subject }) => subject,
                                                textAlign: 'left',
                                                simpleSortable: ({ subject }) => subject
                                            }, {
                                                title: 'númer',
                                                dataAccessor: ({ advertNumber }) => advertNumber,
                                                textAlign: 'left',
                                                simpleSortable: ({ advertNumber }) => advertNumber
                                            }, {
                                                title: 'sýnishorn',
                                                dataAccessor: ({ highlight }) => 
                                                    (highlight !== null) 
                                                        ? highlight.substring(0, Math.min(20, highlight.length))
                                                        : "Á ekki við",
                                                textAlign: 'left',
                                            }, {
                                                title: '',
                                                dataAccessor: (advert) => (
                                                    <Button 
                                                        showLoader={isItemLoading(advert)}
                                                        onClick={() => {tryPurchaseItem(advert, 'confirm-modal-with-reference')}}
                                                        size='sm'
                                                        buttonType='buy'
                                                    >
                                                        Kaupa
                                                    </Button>
                                                ),
                                                textAlign: 'right'
                                            }
                                        ]}
                                    />
                                    : (isSearchLoading)
                                    ? <Loading />
                                    : (dataToDisplay.dataStatus === 'no data')
                                    ? <Alert type='info' headText={`Engar niðurstöður fundust fyrir leitarorð: ${searchTerm}`} />
                                    : (dataToDisplay.dataStatus === "no filter selected")
                                    ? <Alert type='info' headText='Vinsamlegast veljið tegund' />
                                    : <Alert type='alert' headText={ErrorMessages.Other} />
                                }
                            </DisplayBox>
                        </div>
                    </div>
                )}
            </div>
            <ModalsAndToasts />
        </div>
    );
}

export default LegalSearch;

interface IFormValues {
    term: string,
    dateFrom: string,
    dateTo: string,
}

interface ISubFormProps {
    values: IFormValues,
    loading: boolean,
    isAuthenticated?: boolean,
}

const SubFormComponent: React.FC<ISubFormProps> = ({
    values,
    loading,
    isAuthenticated,
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    {(!isAuthenticated)
                        ?   <Tooltip
                                tooltip={
                                    <span>
                                        Þú þarft að skrá þig inn til að nýta þessa þjónustu.
                                    </span>
                                }
                                text={
                                    <div className='searchWrapper'>
                                        <Input
                                            label='Leitarorð'
                                            name="term"
                                            id="term"
                                            value={values.term}
                                            disabled
                                        />
                                        <div className='dateWrapper'>
                                            <DatePickerField
                                                notResponsive
                                                name="dateFrom"
                                                value={values.dateFrom}
                                                label='Dags.Frá'
                                                disabled
                                                />
                                            <DatePickerField
                                                notResponsive
                                                name="dateTo"
                                                value={values.dateTo}
                                                label='Dags.Til'
                                                disabled/>
                                            <Button
                                                showLoader={loading}
                                                size="lg"
                                                disabled>Leita</Button>
                                        </div>
                                    </div>
                                }
                            />
                        : <div className='searchWrapper'>
                            <Input
                                label='Leitarorð'
                                name="term"
                                id="term"
                                value={values.term}
                            />
                            <div className='dateWrapper'>
                                <DatePickerField
                                    notResponsive
                                    name="dateFrom"
                                    value={values.dateFrom}
                                    label='Dags.Frá'
                                    maxDate={new Date()}
                                />
                                <DatePickerField
                                    notResponsive
                                    name="dateTo"
                                    value={values.dateTo}
                                    label='Dags.Til'
                                    maxDate={new Date()}
                                />
                                <Button
                                    showLoader={loading}
                                    size="lg"
                                    disabled={values.term === ''}
                                >
                                    Leita
                                </Button>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </Form>
    );
}
